import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";
import { useAlert } from "react-alert";

import clsx from "clsx";
import * as Sentry from "@sentry/browser";

import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

import useCurrentEnvironment from "../../hooks/use-environment";
import { makeRequest } from "../../util";
import { fetchAccountMetadata } from "../../actions/environments";

import Navigation from "../../Navigation";
import EmptyState from "./EmptyState";
import UnauthorizedState from "../../components/users/Unauthorized";

import TopBar from "./TopBar";
import RulesList from "./RulesList";
import RulesEditor from "./RulesEditor";
import { ALLOWLIST_PAGE_BASE_PATH } from "./common";
import useAccountMetadata from "../../hooks/useAccountMetadata";
import useUsers from "../../hooks/useUsers";

import FreshpaintUser from "../../components/users/FreshpaintUser";
import useFeatureFlags from "../../hooks/useFeatureFlag";

const useStyles = makeStyles(() => ({
  content: {
    display: "flex",
    flex: "1 1 auto",
    overflow: "hidden",
  },
  widget: {},
  widgets: {
    overflowY: "auto",
    paddingTop: 1,
    margin: 0,
  },
}));

function AllowListPage({ allowlistRules }) {
  const classes = useStyles();
  const history = useHistory();
  const alert = useAlert();
  const dispatch = useDispatch();
  const { ruleID: routeRuleID } = useParams();

  const [searchString, setSearchString] = useState("");
  const [unsavedEdits, setUnsavedEdits] = useState(false);
  const [editorState, setEditorState] = useState({});

  const usersState = useUsers();
  const flag = useFeatureFlags();

  const handleSelectRule = (rule) => {
    setEditorState(rule);
    setUnsavedEdits(false);
  };

  const handleNewRule = useCallback(() => {
    history.push(`${ALLOWLIST_PAGE_BASE_PATH}/new`);
  }, []);

  useEffect(() => {
    if (!routeRuleID) {
      if (allowlistRules.length > 0) {
        history.push(`${ALLOWLIST_PAGE_BASE_PATH}/${allowlistRules[0].id}`);
      }
    } else if (routeRuleID === "new") {
      setEditorState({});
      setUnsavedEdits(false);
    } else {
      const foundRule = allowlistRules.find((rule) => rule.id == routeRuleID);
      if (!foundRule) {
        history.push(ALLOWLIST_PAGE_BASE_PATH);
      } else {
        handleSelectRule(foundRule);
      }
    }
  }, [routeRuleID, allowlistRules]);

  // calls the backend to update the allowlist
  const saveAllowlist = async (newAllowlistRules, redirectPath) => {
    const saveInProgressAlert = alert.info("Updating Allowlist ...", {
      timeout: 0,
    });
    try {
      await makeRequest("update_network_capture_allowlist", {
        allowlist: {
          rules: newAllowlistRules,
        },
      });

      // for some reason vscode complains that await isn't necessary but it is
      // we want to make sure the updated allowlist is loaded before we make the redirect
      await dispatch(fetchAccountMetadata(alert));

      setUnsavedEdits(false);
      alert.info("Allowlist Updated");

      if (redirectPath) {
        history.push(redirectPath);
      }
    } catch (err) {
      console.log("Error while updating allowlist: ", err);
      Sentry.captureException(err);
      alert.error("Unable to update allowlist");
    } finally {
      alert.remove(saveInProgressAlert);
    }
  };

  const handleSave = useCallback(
    (rule) => {
      let newAllowlist;
      let redirectPath;
      if (routeRuleID === "new") {
        newAllowlist = [...allowlistRules, rule];
        redirectPath = `${ALLOWLIST_PAGE_BASE_PATH}/${rule.id}`;
      } else {
        const updateIndex = allowlistRules.findIndex(
          (oldRule) => oldRule.id == rule.id
        );
        if (updateIndex === -1) {
          alert.error("Unable to save");
          return;
        }
        newAllowlist = [
          ...allowlistRules.slice(0, updateIndex),
          rule,
          ...allowlistRules.slice(updateIndex + 1),
        ];
      }
      saveAllowlist(newAllowlist, redirectPath);
    },
    [routeRuleID, allowlistRules]
  );

  const handleDelete = useCallback(
    (ruleID) => {
      const deleteIndex = allowlistRules.findIndex(
        (oldRule) => oldRule.id == ruleID
      );
      if (deleteIndex === -1) {
        alert.error("Unable to delete");
        return;
      }
      const newAllowlist = [
        ...allowlistRules.slice(0, deleteIndex),
        ...allowlistRules.slice(deleteIndex + 1),
      ];

      // this is a slightly tricky bit. I want to know which rule to open when one is deleted
      let redirectPath = ALLOWLIST_PAGE_BASE_PATH;
      if (newAllowlist.length > 0) {
        const toOpenRuleIndex = deleteIndex > 0 ? deleteIndex - 1 : 0;
        const toOpenRuleId = newAllowlist[toOpenRuleIndex].id;
        redirectPath = `${ALLOWLIST_PAGE_BASE_PATH}/${toOpenRuleId}`;
      }

      saveAllowlist(newAllowlist, redirectPath);
    },
    [allowlistRules]
  );

  if (usersState.loading) {
    return <>Loading</>;
  }

  if (
    flag("rbac") &&
    !new FreshpaintUser(usersState.currentUser).hasAdminAccess()
  ) {
    return (
      <UnauthorizedState message="Only Admin can view / update API Allowlist" />
    );
  }

  if (routeRuleID !== "new" && allowlistRules.length == 0) {
    return <EmptyState onNewRule={handleNewRule} />;
  }

  return (
    <>
      <TopBar
        searchString={searchString}
        onSearchStringChange={setSearchString}
        onNewRule={handleNewRule}
        showHeading={true}
        heading={`Rule / ${editorState.name || ""}`}
      />
      <div className={classes.content}>
        <RulesList
          rules={allowlistRules}
          activeRuleID={editorState.id}
          searchString={searchString}
        />
        <Grid
          container
          spacing={0}
          alignItems="flex-start"
          alignContent="flex-start"
          className={clsx(classes.widgets, "px-5 pb-2")}
        >
          <Grid item xs={8}>
            <div className={classes.widget}>
              <RulesEditor
                editorType={routeRuleID === "new" ? "create" : "edit"}
                unsavedEdits={unsavedEdits}
                setUnsavedEdits={setUnsavedEdits}
                editorState={editorState}
                setEditorState={setEditorState}
                onSave={handleSave}
                onDelete={handleDelete}
              />
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
}

const navigationStyles = makeStyles(() => ({
  navigation: {
    paddingLeft: 0,
    paddingRight: 0,
    height: "100%",
    display: "flex",
    flexFlow: "column",
  },
}));

function AllowListPageWithNav({}) {
  const classes = navigationStyles();
  const currentEnvironment = useCurrentEnvironment();

  return (
    <Navigation className={classes.navigation}>
      {currentEnvironment.loaded ? (
        <AllowListPage
          allowlistRules={
            (
              currentEnvironment.project.config.networkCaptureAllowlist || {
                rules: [],
              }
            ).rules
          }
        />
      ) : (
        <>Loading</>
      )}
    </Navigation>
  );
}

export default AllowListPageWithNav;
export { ALLOWLIST_PAGE_BASE_PATH };
